// TelcoTrack — import → catalog/apply: default zapnuto, fail-closed report, bez copy-paste apply logiky.

export interface AuthorityCatalogApplyReport {
  applied: boolean;
  created: number;
  skipped: number;
  error: string | null;
}

/** Per-polygon apply. Missing key = default true. Explicit `false` skips apply. */
export type AuthorityCatalogApplyByPolygon = Record<string, boolean>;

/** Source key for a polygon created in this import job (client does not know the UUID yet). */
export const AUTHORITY_CATALOG_APPLY_NEW_KEY = 'new';

export const AUTHORITY_CATALOG_APPLY_UNAVAILABLE_CS =
  'Služba Úřady není dostupná. Katalog se nedoplnil.';

export interface AuthorityCatalogApplyResolveOptions {
  /** True when this job created the polygon (GeoJSON „Nový polygon“). */
  createdInJob?: boolean;
}

/** Chybějící job-wide flag = zapnuto. Jen explicitní `false` apply vypne. */
export function shouldApplyAuthorityCatalog(flag?: boolean | null): boolean {
  return flag !== false;
}

function hasOwnApplyKey(
  map: AuthorityCatalogApplyByPolygon,
  key: string,
): boolean {
  return Object.prototype.hasOwnProperty.call(map, key);
}

/**
 * Coerce request body map. Only explicit true/false (and common string/number forms) become keys.
 * Invalid values are ignored (missing key → default apply).
 */
export function parseAuthorityCatalogApplyByPolygon(
  value: unknown,
): AuthorityCatalogApplyByPolygon | undefined {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const out: AuthorityCatalogApplyByPolygon = {};
  let any = false;
  for (const [rawKey, raw] of Object.entries(value as Record<string, unknown>)) {
    const key = rawKey.trim();
    if (!key) continue;
    if (raw === false || raw === 'false' || raw === 0 || raw === '0') {
      out[key] = false;
      any = true;
    } else if (raw === true || raw === 'true' || raw === 1 || raw === '1') {
      out[key] = true;
      any = true;
    }
  }
  return any ? out : undefined;
}

export function buildAuthorityCatalogApplyByPolygon(
  polygonId: string | null | undefined,
  apply: boolean,
  options?: AuthorityCatalogApplyResolveOptions,
): AuthorityCatalogApplyByPolygon {
  const id = (polygonId ?? '').trim();
  if (id) return { [id]: apply !== false };
  if (options?.createdInJob) return { [AUTHORITY_CATALOG_APPLY_NEW_KEY]: apply !== false };
  return {};
}

/**
 * Per-polygon flag wins. Missing key in a provided map = apply (default true).
 * No map at all → job-wide boolean fallback (`shouldApplyAuthorityCatalog`).
 */
export function shouldApplyAuthorityCatalogForPolygon(
  polygonId: string | null | undefined,
  byPolygon?: AuthorityCatalogApplyByPolygon | null,
  jobFlag?: boolean | null,
  options?: AuthorityCatalogApplyResolveOptions,
): boolean {
  const map = byPolygon && typeof byPolygon === 'object' && !Array.isArray(byPolygon)
    ? byPolygon
    : null;
  if (map) {
    const id = (polygonId ?? '').trim();
    if (id && hasOwnApplyKey(map, id)) return map[id] !== false;
    if (options?.createdInJob && hasOwnApplyKey(map, AUTHORITY_CATALOG_APPLY_NEW_KEY)) {
      return map[AUTHORITY_CATALOG_APPLY_NEW_KEY] !== false;
    }
    return true;
  }
  return shouldApplyAuthorityCatalog(jobFlag);
}

export function skippedAuthorityCatalogApplyReport(): AuthorityCatalogApplyReport {
  return { applied: false, created: 0, skipped: 0, error: null };
}

export function failedAuthorityCatalogApplyReport(
  error?: string | null,
): AuthorityCatalogApplyReport {
  const message = (error ?? '').trim() || AUTHORITY_CATALOG_APPLY_UNAVAILABLE_CS;
  return { applied: false, created: 0, skipped: 0, error: message };
}

export function authorityCatalogApplyReportFromResult(input: {
  created?: readonly unknown[] | number | null;
  skipped?: number | null;
}): AuthorityCatalogApplyReport {
  const created = Array.isArray(input.created)
    ? input.created.length
    : Number(input.created ?? 0);
  const skipped = Number(input.skipped ?? 0);
  return {
    applied: true,
    created: Number.isFinite(created) ? created : 0,
    skipped: Number.isFinite(skipped) ? skipped : 0,
    error: null,
  };
}

export async function runAuthorityCatalogApplyIfRequested(
  flag: boolean | null | undefined,
  apply: () => Promise<{ created?: readonly unknown[] | number | null; skipped?: number | null }>,
): Promise<AuthorityCatalogApplyReport> {
  if (!shouldApplyAuthorityCatalog(flag)) return skippedAuthorityCatalogApplyReport();
  try {
    return authorityCatalogApplyReportFromResult(await apply());
  } catch (err) {
    return failedAuthorityCatalogApplyReport(err instanceof Error ? err.message : String(err));
  }
}

export function formatAuthorityCatalogApplyReportCs(
  report: AuthorityCatalogApplyReport | null | undefined,
): string | null {
  if (!report) return null;
  if (report.error) return `Úřady z katalogu: chyba — ${report.error}`;
  if (!report.applied) return 'Úřady z katalogu: přeskočeno.';
  return `Úřady z katalogu: založeno ${report.created}, přeskočeno ${report.skipped}.`;
}
